import * as Pck from '../spinnaxBase/logic/Com_BLE_packets';
import { Instance } from 'mobx-state-tree';

import * as ST from './storage';

import * as COM from './communication';
export { ST };
export { COM };

export type Reduced<E> = { [P in keyof E]: E[P] };
export type MSTInstance<E> = Reduced<Instance<E>>;

export type Mutable<Type> = {
  -readonly [Key in keyof Type]: Type[Key];
};

type IfEquals<X, Y, A = X, B = never> = (<T>() => T extends X ? 1 : 2) extends <
  T
>() => T extends Y ? 1 : 2
  ? A
  : B;

export type WritableKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    P
  >;
}[keyof T];
export type ReadonlyKeys<T> = {
  [P in keyof T]-?: IfEquals<
    { [Q in P]: T[P] },
    { -readonly [Q in P]: T[P] },
    never,
    P
  >;
}[keyof T];

export interface ClassConstructor<T> {
  new (...args: any[]): T;
  prototype: T;
}

export interface Bufferable {
  toArrayBuffer(buffer: ArrayBuffer, index?: number): ArrayBuffer;
}
export interface ByteCountable<T> extends ClassConstructor<T> {
  byteCount(): number;
  ofArrayBuffer(buffer: ArrayBuffer, offset?: number): T;
}

export type Spin = '' | '180' | '360' | '540' | '720' | '900';

export const SpinValues: Spin[] = ['', '180', '360', '540', '720', '900'];

export type Stance = 'Normal' | 'Switch' | 'Nollie' | 'Fakie';

export const Stances: Stance[] = ['Normal', 'Switch', 'Nollie', 'Fakie'];

export interface PlayData {
  information?: ST.PlayDetails;
  steps: ST.PlayStep[];
}

export type { BluetoothEntry } from '../models/BluetoothInfo';

export type { FreakInfo } from '../models/BluetoothInfo';

export type PckType = Reduced<typeof Pck>;
export type PckKeys = keyof PckType;

export type BLCmds = {
  [Property in keyof PckType as Property &
    `PckCmd${string}`]: PckType[Property];
};
export type BLCmdType = PckType[keyof BLCmds];
export type BLCmd = InstanceType<BLCmdType>;

export type BLRsps = {
  [Property in keyof PckType as Property &
    `PckRsp${string}`]: PckType[Property];
};
export type BLRspType = PckType[keyof BLRsps];
export type BLRsp = InstanceType<BLRspType>;

export type ZephyrStatus =
  | 'started'
  | 'stateChanged'
  | 'upgradeStarted'
  | 'upgradeCompleted'
  | 'upgradeFailed'
  | 'upgradeCanceled'
  | 'uploadProgressChanged'
  | 'requestMcuMgrParameters'
  | 'validate'
  | 'none';

export interface ZephyrUploadProgress {
  bytesSent: number;
  imageSize: number;
}

export interface ZephyrStateUpdate {
  prevState: ZephyrStatus;
  newState: ZephyrStatus;
}

export interface ZephyrErrorData {
  msg: string;
}

export type ZephyrData =
  | ZephyrUploadProgress
  | ZephyrStateUpdate
  | ZephyrErrorData;

export interface ZephyrResponse {
  status: ZephyrStatus;
  data?: ZephyrData;
}

export enum _BLEStatus {
  NOT_FOUND = 10,
  NOT_READY = 20,
  CONFIGURATION_FAILED = 30,
  RESET_FAILED = 40,
  THREAD_CREATION_FAILED = 50,
  ACTION_FAILED = 60,
  NOT_INITIALIZED = 255,
}
export type BLEStatus = keyof typeof _BLEStatus;

export enum _LogLevel {
  FATAL = 60,
  CRITICAL = 50,
  ERROR = 40,
  WARNING = 30,
  LOG = 25,
  INFO = 20,
  DEBUG = 10,
  NOT_SET = 0,
}
export type LogLevel = keyof typeof _LogLevel;

export interface BLEDongleState {
  versionOS: string;
  versionFW: string;
  statusUSB: BLEStatus;
  statusBLE: BLEStatus;
  statusBNO08X: BLEStatus;
  statusDPS310: BLEStatus;
  statusLED: BLEStatus;
  statusButton: BLEStatus;
  statusBattery: BLEStatus;
  /** battery level in Percent */
  batteryLevel: number;
}
